import React, { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthCtx = createContext(null);

// SEC: user profile is cached in sessionStorage for a snappy UX hydration
// (survives page reload within the same tab). Authentication itself is driven
// by the httpOnly session cookie — we always verify with /auth/me on mount.
const USER_CACHE_KEY = "propay_user";

function readCachedUser() {
  try {
    const raw = sessionStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(u) {
  try {
    if (u) sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    else sessionStorage.removeItem(USER_CACHE_KEY);
  } catch (err) {
    console.warn("Session cache unavailable (storage disabled):", err);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readCachedUser());
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    // Verify the session cookie is still valid; refresh cached user.
    let cancelled = false;
    api
      .get("/auth/me")
      .then((r) => {
        if (cancelled) return;
        setUser(r.data);
        writeCachedUser(r.data);
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        writeCachedUser(null);
      })
      .finally(() => !cancelled && setBootstrapped(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = (data) => {
    setUser(data.user);
    writeCachedUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return persist(data);
  };

  const otpRequest = async (email, mobile) => {
    const { data } = await api.post("/auth/otp/request", { email, mobile });
    return data; // includes mocked email_otp + phone_otp when DEMO_MODE=true
  };

  const otpVerify = async (payload) => {
    const { data } = await api.post("/auth/otp/verify", payload);
    return persist(data);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Cookie clear should succeed even if the server is slow; keep going.
      console.warn("logout endpoint failed:", err?.message);
    }
    setUser(null);
    writeCachedUser(null);
    // Clear the PunchOut return marker so a manual sign-out doesn't leave a
    // stale "Return to One Buy" chip on the login screen.
    try { sessionStorage.removeItem("propay_punchout_source"); } catch (err) { console.warn("Could not clear PunchOut marker:", err); }
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider
      value={{ user, bootstrapped, login, otpRequest, otpVerify, logout, setUser }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

export function homeForRole(role) {
  if (!role) return "/login";
  if (role === "influencer") return "/influencer";
  if (role === "procurement_desk") return "/procurement";
  if (role === "propay_admin") return "/admin";
  if (role === "customer") return "/customer";
  if (role === "ey_auditor") return "/ey";
  if (role === "hul_approver") return "/approver";
  return "/login";
}
